<template>
  <a-spin :spinning="loading">
    <div :class="{ 'not-allowed-cursor': disabled }">
      <section class="section">
        <ChatInput
          ref="inputRef"
          :file="fileMetaData"
          :files="filesMetaData"
          :tooltip-text="tooltipText"
          :disabled="disabled"
          @update:message="inputMessage = $event"
          @submit="handleSubmit"
          @delete-file="handleDeleteFile"
          @paste-file="setFile($event)"
          @handle-event-global="handleEventGlobal"
          @blur="handleBlur"
        />

        <div :class="{ 'not-allowed-cursor': hasFileOrRecordFile, 'actions-group': true }">
          <div class="actions" :class="{ 'no-events': disabled || hasFileOrRecordFile }">
            <EmojiPicker @onChangeEmoji="handleSelectEmoji" @handleShowPicker="handleShowPicker" />

            <label for="file-input">
              <ActionButton
                :disabled="hasFilesLimit"
                type="file"
                prevent-event
                @click="handleEventWriting"
              />

              <input
                type="file"
                id="file-input"
                ref="fileInputRef"
                multiple
                :accept="supportedFileTypes.join()"
                @change="handleFileInputChange($event)"
              />
            </label>

            <label for="media-input">
              <ActionButton
                :disabled="hasFilesLimit"
                type="media"
                prevent-event
                @click="handleEventWriting"
              />

              <input
                ref="mediaInputRef"
                id="media-input"
                type="file"
                multiple
                :accept="supportedMediaTypesWhatsapp.join()"
                @change="handleFileInputChange($event, 'image')"
              />
            </label>

            <ActionButton
              v-if="channelId === ChannelType.Whatsapp"
              :disabled="hasFiles"
              type="template"
              @click="handleTemplateClick"
            />
            <ActionButton
              :disabled="hasFiles"
              type="quick-answers"
              @click="
                openQuickAnswersModal = true;
                handleEventWriting();
              "
            />
          </div>
          <span class="ticket__managed-by" v-if="channel?.channel_customer?.alias == 'Beex' && !ticket?.is_first_open" >
            Gestionado por BeexAI
            <IconIAIntegrationSmallColor style="width: 12px; height: 12px;" />
          </span>
        </div>
      </section>
    </div>
  </a-spin>

  <SendWhatsappTemplateModal
    ref="whatsappTemplateModal"
    chat-button
    :record="{ ...contactInfoForTemplate, id: client?.id, code_country: channel?.code_country }"
    :ticketQueue="ticket?.queue"
    :portfolio="ticket?.portfolio"
    :selectedInteraction="channel"
    :ticketId="ticket?.id"
  />

  <SelectQuickAnswerModal
    v-if="openQuickAnswersModal"
    v-model:open="openQuickAnswersModal"
    :queue="channel?.queue?.id ?? ticket?.queue?.id"
    @select:answer="handleAddQuickAnswer($event)"
    @handleClose="handleCloseQuickAnswer"
  />
</template>

<script setup lang="ts">
import { ref, reactive, computed, inject, onBeforeUnmount, watch, nextTick, onUnmounted } from "vue";
import type { Ref } from "vue";
import { useRoute } from "vue-router";
import { message } from "ant-design-vue";
import { useStore } from "vuex";
import axios from "@/plugins/axios";
import { debounce } from "lodash";

import type { Emoji, FileMetadata } from "@/@types/workspace";
import type { Ticket, Interaction } from "@/@types/global";
import { supportedMediaTypesWhatsapp, supportedFileTypes } from "./supportedFiles";
import { sendWorkspaceMessage } from "@/app/workspace/services";
import { ChannelType, MessageType } from "@/app/shared/utils/enums";
import { QuickAnswer } from "@/@types/platformSettings/quickAnswers";

import ChatInput from "./ChatInput.vue";
import ActionButton from "./ActionButton.vue";
import SendWhatsappTemplateModal from "@/app/workspace/components/modal/SendWhatsappTemplateModal.vue";
import EmojiPicker from "@/app/shared/components/pickers/EmojiPicker.vue";
import SelectQuickAnswerModal from "../../modal/SelectQuickAnswerModal.vue";
import { useChatStorage } from "@/app/workspace/store/chatStorage";

import { EventManagement } from "@/app/shared/utils/enums";
import { registerEventManagement } from "@/app/shared/services";
import IconIAIntegrationSmallColor from "@/app/common/components/atoms/IconIAIntegrationSmallColor.vue";
import { ResponseError } from "@/app/shared/utils/httpManager";
import { safeRegisterEventManagement } from "@/app/shared/composables/useRegisterEventManagement.ts";

interface ContactInfo {
  phone?: string | null;
  picture?: string | null;
  full_name: string;
  whatsapp_username?: string | null;
}

const LIMIT_FILES = 5;
const OneMegaByte = 1_000_000;
const route = useRoute();
const store = useStore();

const props = defineProps<{
  channelId?: number;
  tooltipText: string;
  disabled: boolean;
}>();
const emit = defineEmits<{
  (event: "sended"): void;
}>();

const client = computed(() => store.getters["getWorkspaceClient"]);

const inputRef = ref<InstanceType<typeof ChatInput> | null>(null);
const fileInputRef = ref<HTMLInputElement | null>(null);
const mediaInputRef = ref<HTMLInputElement | null>(null);
const responseContext = inject("responseContext") as Ref<any>;

const fileMetaData: FileMetadata = reactive({
  file: null,
  inputType: null,
});

// const fileInput: FileMetadata = reactive({
//   file: null,
//   inputType: null,
// });

const filesMetaData: FileMetadata[] = reactive([]);

const hasFileOrRecordFile = computed(
  () => fileMetaData.file || inputRef.value?.getRecordMetadata()?.recordingFile
);

const invalidFiles = ref<File[]>([]);

const hasFiles = computed(() => filesMetaData.length > 0);

const hasFilesLimit = computed(() => filesMetaData.length == LIMIT_FILES);

const handleSelectEmoji = (emojiMetadata: Emoji) => {
  inputRef.value?.addCharToInputContent(emojiMetadata.i);
  registerEvent(EventManagement.Writing);
};

const handleEventWriting = () => {
  registerEvent(EventManagement.Writing);
};

const handleCloseQuickAnswer = (event) => {
  // Si es que no se agregó nada
  if (!event) handleEventGlobal();
};

// const globalState = computed(() => store.getters["getStateUser"]);
const handleEventGlobal = async () => {
  await nextTick();
  const message = inputRef.value?.textInputRef?.textContent;
  if ((message == "" || !message) && fileMetaData?.file == null && filesMetaData?.length == 0)
    registerEvent(EventManagement.Opened);
  // registerEvent(store.getters["getStateUser"]);
};

const setFile = (file: File | undefined) => {
  try {
    if (filesMetaData.length < LIMIT_FILES) {
      validateFile(file);
      const fileInput = { file: undefined, inputType: "" };
      fileInput.file = file;

      if (file?.type.includes("video")) fileInput.inputType = "video";
      else if (file?.type.includes("image")) fileInput.inputType = "image";
      else fileInput.inputType = "file";
      filesMetaData.push(fileInput);
      handleEventWriting();
    }
  } catch (error) {
    console.error(error);
    message.error(error as string);
  }
};

const handleFileInputChange = (event: Event, type?: string) => {
  const target = event.target as HTMLInputElement;
  // const selectedFile = target.files?.[0];
  const fileList = target.files;
  const filesArray = Array.from(fileList);

  filesArray.forEach((file: any) => {
    console.log("Archivo seleccionado:", file.name, file.type);
    setFile(file);
    // if (supportedMediaTypesWhatsapp.includes(file.type) || type != 'image') {
    //   setFile(file);
    // } else {
    //   invalidFiles.value.push(file);
    //   message.error(`Uno o más archivos no son permitidos (Solo jpg, jpeg, png, y videos)`);
    // }
  });
};

const validateFile = (file: File | undefined) => {
  if (!file) throw "No se seleccionó ningun archivo";

  if (file.size > 5 * OneMegaByte) throw "Supera el tamaño máximo de 5MB";
};

const handleDeleteFile = (position?: number) => {
  if (position != undefined && position > -1) {
    filesMetaData.splice(position, 1);
  } else {
    filesMetaData.splice(0, filesMetaData.length);
    fileMetaData.file = null;
    fileMetaData.inputType = null;
    if (fileInputRef.value) fileInputRef.value.value = "";
    if (mediaInputRef.value) mediaInputRef.value.value = "";
  }
};

const ticket = inject("selectedTicket") as Ref<Ticket>;
const channel = inject("selectedChannel") as Ref<Interaction>;
const contactInfoForTemplate = computed<ContactInfo>(() => {
  const currentTicket = ticket?.value;
  const currentChannel = channel?.value;

  // Si ya hay interaccion/canal resuelto, se confia en SU identifier aunque
  // venga vacio: un ticket puede agrupar mas de una interaccion de WhatsApp y
  // ticket.identifier puede traer el telefono de OTRA interaccion (propagacion
  // CU-86e30a03u). Mismo criterio que WorkspaceChatHeader.vue:phoneIdentifier.
  const rawPhone = currentChannel ? currentChannel?.identifier : currentTicket?.identifier;
  const isValidPhone =
    rawPhone &&
    !String(rawPhone).startsWith("@") &&
    String(rawPhone).trim() !== "" &&
    String(rawPhone).toLowerCase() !== "null" &&
    String(rawPhone).toLowerCase() !== "undefined";

  const rawUsername =
    currentChannel?.whatsapp_username ||
    currentTicket?.whatsapp_username ||
    (String(currentTicket?.identifier).startsWith("@") ? currentTicket?.identifier : null) ||
    (String(currentChannel?.identifier).startsWith("@") ? currentChannel?.identifier : null);

  return {
    phone: isValidPhone ? String(rawPhone).trim() : null,
    picture: currentTicket?.client?.profile_picture,
    full_name: currentTicket?.client?.name || "",
    whatsapp_username: rawUsername ? String(rawUsername).trim() : null,
  };
});

const whatsappTemplateModal = ref<InstanceType<typeof SendWhatsappTemplateModal> | null>(null);

const handleTemplateClick = () => {
  whatsappTemplateModal.value?.openWhatsModal();
  handleEventWriting();
};

// Quick Answers Logic
const openQuickAnswersModal = ref(false);

const handleAddQuickAnswer = (answer: QuickAnswer) => {
  // fileMetaData.file = answer.file_url;
  // fileMetaData.inputType = answer.type_file;
  const fileTempData = {
    file: answer.file_url,
    inputType: answer.type_file,
  };
  filesMetaData.push(fileTempData);
  inputRef.value?.addCharToInputContent(answer.message);
};

// Submit Logic
const loading = ref<boolean>(false);
const inputMessage = ref<string>("");

// Typing Indicator Logic (Flow A)
let lastSentStatus: "start" | "stop" | null = null;
let activeTypingSessionId: string | null = null;

const sendTypingState = async (status: "start" | "stop", sessionIdOverride?: string) => {
  const sessionId = sessionIdOverride || ticket.value?.identifier;
  if (!sessionId) return;

  if (status === "start" && lastSentStatus === "start" && activeTypingSessionId === sessionId) return;
  if (status === "stop" && lastSentStatus === "stop") return;

  if (props.channelId !== ChannelType.Inbound) return;

  try {
    if (status === "start") {
      lastSentStatus = "start";
      activeTypingSessionId = sessionId;
    } else {
      lastSentStatus = "stop";
      activeTypingSessionId = null;
    }

    await axios.post("/webhooks/inbound/typing-agent/", {
      chatSessionId: sessionId,
      status,
    });
  } catch (error: any) {
    console.error(error);
  }
};

const debouncedStartTyping = debounce(() => {
  sendTypingState("start");
}, 200);

const debouncedStopTyping = debounce(() => {
  sendTypingState("stop");
}, 2000);

const handleBlur = () => {
  sendTypingState("stop");
  debouncedStartTyping.cancel();
  debouncedStopTyping.cancel();
};

watch(inputMessage, (newVal) => {
  if (newVal && newVal.trim().length > 0) {
    debouncedStartTyping();
    debouncedStopTyping();
  } else {
    debouncedStartTyping.cancel();
    debouncedStopTyping.cancel();
    sendTypingState("stop");
  }
});

watch(() => ticket.value?.id, (newId, oldId) => {
  if (oldId && activeTypingSessionId) {
    sendTypingState("stop", activeTypingSessionId);
  }
  debouncedStartTyping.cancel();
  debouncedStopTyping.cancel();
});

const getMessageType = (payload: { message: string }, file?: FileMetadata) => {
  if (inputRef.value?.getRecordMetadata()?.recordingFile) return MessageType.Audio;

  // const fileType = fileMetaData.inputType;
  const fileType = file ? file.inputType : filesMetaData?.[0]?.inputType;

  if (payload.message && !fileType) return MessageType.Text;
  if (fileType && !payload.message) {
    if (fileType === "image") return MessageType.Image;
    if (fileType === "video") return MessageType.Video;
    if (fileType === "file") return MessageType.File;
  }

  if (fileType === "image") return MessageType.TextWithImage;
  if (fileType === "video") return MessageType.TextWithVideo;
  if (fileType === "file") return MessageType.TextWithFile;
};

const handleSubmit = async () => {
  if (loading.value) return;

  sendTypingState("stop");
  debouncedStartTyping.cancel();
  debouncedStopTyping.cancel();

  loading.value = true;

  try {
    const ticket = route.query.ticket;
    const interaction = route.query.interaction;

    const audioFile = inputRef.value?.getRecordMetadata()?.recordingFile;
    const fileToSent = audioFile || fileMetaData.file || filesMetaData?.[0]?.file;

    const basePayload = {
      message: inputMessage.value,
      interaction,
      ticket,
    };

    const fileKey = typeof fileToSent === "string" ? "file_url" : "file";
    const messageType = getMessageType(basePayload);

    console.log({ file: fileToSent, type: typeof fileToSent });

    console.log(responseContext, "responseContext");
    await sendWorkspaceMessage({ 
      ...basePayload,
      [fileKey]: fileToSent,
      type: messageType,
      context_message: responseContext.value ? responseContext.value?.id : null
    });

    if (filesMetaData.length > 1) {
      basePayload.message = "";
      for (let i = 1; i < filesMetaData.length; i++) {
        const fileToSent = filesMetaData?.[i]?.file;
        const fileKey = typeof fileToSent === "string" ? "file_url" : "file";
        const messageType = getMessageType(basePayload, filesMetaData[i]);
        await sendWorkspaceMessage({ ...basePayload, [fileKey]: fileToSent, type: messageType });
      }
    }

    console.log(messageType, "messagetype");
    responseContext.value = null;
    inputRef.value?.handleDeleteMessage(MessageType.Audio == messageType);
    handleDeleteFile();
    chatStorage.deleteInteractionInputData(interactionId.value);

    emit("sended");
  } catch (error: any) {
    console.error(error);
    console.log("error response", error.response);
    if (error.response.status === 400 && error.response.data?.phone_number) {
      // const fullErrorMessage = error.error.response.data;
      // const errorMessage = fullErrorMessage.replace(/^[^:]+:\s*/, "");
      message.error(error.response.data.phone_number);
    } else {
      let messageError;
      if (error.response.data)
        messageError = JSON.stringify(error.response.data).replace(/[{}[\]"]/g, "").replace(/,/g, " ");
      else
        messageError = error instanceof ResponseError ? error.getErrorMessage() : "Ha ocurrido un error";
            
      message.error(messageError);
    }
  }

  loading.value = false;
};

const dataUserState = computed(() => store.getters["getDataUserState"]);
const registerEvent = async (event: number) => {
  if (
    !(
      event == EventManagement.Writing &&
      store.getters["getEventMessaging"] == EventManagement.Writing
    )
  ) {
    store.dispatch("setEventMessaging", event);
    const ticket = route.query.ticket;
    await safeRegisterEventManagement(event, {
      open_ticket: ticket,
      menu: 23,
      state: dataUserState.value,
      interaction_id: event == EventManagement.Writing ? route.query.interaction : undefined,
      module: 'input-section',
    });
  }
};

const chatStorage = useChatStorage();
const interactionId = ref<number>();

watch(
  () => channel.value?.id,
  async (newInteraction) => {
    if (!newInteraction) return;

    interactionId.value = newInteraction;

    await nextTick();

    if (props.disabled) return;
    if (!interactionId.value || !inputRef.value) return;

    const inputData = chatStorage.getInteractionInputData(interactionId.value);

    if (inputData?.message) inputRef.value.addCharToInputContent(inputData.message);
    if (inputData?.file) Object.assign(fileMetaData, inputData.file);
    if (inputData?.files) {
      filesMetaData.splice(0, filesMetaData.length, ...(inputData.files));
    }
    if (inputData?.record) inputRef.value.setRecordMetadata(inputData.record);
  },
  { immediate: true }
);

const saveInteractionData = () => {
  if (!interactionId.value) return;

  chatStorage.saveInteractionInputData(interactionId.value, {
    message: inputMessage.value,
    file: fileMetaData,
    files: filesMetaData,
    record: inputRef.value?.getRecordMetadata(),
  });
};

onBeforeUnmount(() => {
  saveInteractionData();
  if (activeTypingSessionId) {
    sendTypingState("stop", activeTypingSessionId);
  }
  debouncedStartTyping.cancel();
  debouncedStopTyping.cancel();
});

onUnmounted(() => {
  saveInteractionData();
  if (activeTypingSessionId) {
    sendTypingState("stop", activeTypingSessionId);
  }
  debouncedStartTyping.cancel();
  debouncedStopTyping.cancel();
});
</script>

<style lang="sass" scoped>
.not-allowed-cursor
  cursor: not-allowed

.no-events
  pointer-events: none
  opacity: 60%

.section
  padding: 12px 16px
  border-top: 1px solid $gray-5

.actions
  display: flex
  align-items: center
  gap: 12px
  margin-top: 8px
  max-height: 24px

#file-input, #media-input
  display: none

.ticket__managed-by
  font-weight: 400
  font-style: Regular
  font-size: 12px
  leading-trim: NONE
  line-height: 20px
  letter-spacing: 0%
  text-align: right
  vertical-align: middle
  background: linear-gradient(to right, #762ECF, #D62FA0)
  -webkit-background-clip: text
  -webkit-text-fill-color: transparent
  display: flex
  align-items: center
  flex-wrap: wrap
  gap: 4px
  margin-top: 8px

.actions-group
  display: flex
  justify-content: space-between

</style>

<style>
.emoji-popover .ant-popover-inner-content {
  padding: 0 !important;
}

.v3-emoji-picker {
  box-shadow: none !important;
}

.v3-emoji-picker.v3-color-theme-light .v3-footer {
  display: none;
}
</style>
